import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { FileText, Image as ImageIcon, Upload, Trash2, ExternalLink, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { listNtsPaperwork, uploadNtsPaperwork, deleteNtsPaperwork, buildAuthedUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";
import { salvageFull, salvageShort, tenantLocale, tenantTz } from "../lib/company";
import { hasCap } from "../lib/roles";

const fmtSize = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" }) : "—";

/**
 * Dialog for attaching signed sign-over paperwork to an NTS-impound job.
 *
 * Accepts: PDF, JPG, PNG, HEIC, WebP — up to 25 MB per file. Multiple files
 * supported via repeated upload (e.g. front + back of a form).
 *
 * Permissions:
 *   - Upload: admin, dispatcher, viewer (require_staff on backend)
 *   - Delete: admin + dispatcher only (paperwork is legally meaningful, so
 *     the billing user cannot remove records once attached — backend enforces this)
 */
export default function NtsPaperworkDialog({ open, onOpenChange, job }) {
  const { user } = useAuth();
  const canDelete = hasCap(user, "admin", "dispatcher");
  const fileInputRef = useRef(null);
  // Separate input with `capture="environment"` — when tapped on mobile, the
  // OS launches the back camera directly (skips the "Camera vs Files" picker).
  // On desktop it still opens a file dialog as a graceful fallback.
  const scanInputRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const refresh = useCallback(async () => {
    if (!job?.id) return;
    setLoading(true);
    try {
      const data = await listNtsPaperwork(job.id);
      setDocs(data);
    } catch (e) {
      // Oct 2026 — reset docs on failure. Previously the stale list from
      // the LAST-opened row stayed on screen after a 404/403, so clicking
      // a different NTS row made it look like every row shared the same
      // paperwork. Clearing here guarantees the UI reflects "no data for
      // THIS row" instead of silently showing data from another row.
      setDocs([]);
      toast.error(e.response?.data?.detail || "Failed to load paperwork");
    } finally {
      setLoading(false);
    }
  }, [job?.id]);

  useEffect(() => {
    if (open && job?.id) {
      // Oct 2026 — clear stale docs BEFORE fetching the new row's
      // paperwork. The dialog is mounted once at the page level and
      // reused across rows, so `docs` state otherwise leaks between
      // row clicks (visible as "every row shows the same uploaded
      // image once you've opened the row that has one").
      setDocs([]);
      refresh();
    }
  }, [open, job?.id, refresh]);

  const upload = async (files) => {
    if (!files || files.length === 0 || !job?.id) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        // Upload sequentially so a failure halfway through gives a clear toast
        // and we don't end up with phantom progress.
        try {
          const entry = await uploadNtsPaperwork(job.id, f);
          setDocs((d) => [...d, entry]);
          toast.success(`Uploaded ${f.name}`);
        } catch (e) {
          toast.error(`${f.name}: ${e.response?.data?.detail || "Upload failed"}`);
        }
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = ""; // allow re-picking the same file
      if (scanInputRef.current) scanInputRef.current.value = "";
    }
  };

  const doDelete = async (doc) => {
    if (!canDelete) return;
    const ok = window.confirm(`Delete "${doc.filename}"? This permanently removes the file.`);
    if (!ok) return;
    try {
      await deleteNtsPaperwork(job.id, doc.id);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      toast.success("Paperwork removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(e.dataTransfer.files);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nts-paperwork-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-600" /> {salvageShort()} Sign-Over Paperwork
          </DialogTitle>
          <DialogDescription>
            {job?.vehicle ? <strong className="text-slate-800 dark:text-slate-100">{job.vehicle}</strong> : "Vehicle"}
            {job?.rego && <> · <span className="font-mono">{job.rego}</span></>}
            <br />
            <span className="text-xs text-slate-500">
              Attach the signed transfer form. PDF or photo, up to 25 MB per file.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
        {/* Quick "Scan with Camera" — mobile-first shortcut that bypasses the
            "Camera vs Files" picker by setting capture="environment" on the
            hidden input. On desktop falls back gracefully to a file dialog. */}
        <button
          type="button"
          onClick={() => scanInputRef.current?.click()}
          disabled={uploading}
          className={cn(
            "w-full bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white font-bold uppercase tracking-wide",
            "py-3 rounded-md flex items-center justify-center gap-2 transition-colors shadow-md",
            uploading && "opacity-60 cursor-wait",
          )}
          data-testid="nts-paperwork-scan-btn"
          title="Open the back camera to scan a signed form"
        >
          <Camera className="h-5 w-5" />
          Scan with Camera
        </button>
        <input
          ref={scanInputRef}
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          className="hidden"
          onChange={(e) => upload(e.target.files)}
          data-testid="nts-paperwork-scan-input"
        />

        {/* Upload zone — drag-drop OR click-to-browse (for pre-scanned PDFs) */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "border-2 border-dashed rounded-md p-5 text-center cursor-pointer transition-colors",
            dragOver
              ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30"
              : "border-slate-300 dark:border-slate-700 hover:border-amber-400 hover:bg-amber-50/50 dark:hover:bg-amber-950/20",
            uploading && "opacity-60 cursor-wait",
          )}
          data-testid="nts-paperwork-dropzone"
        >
          <Upload className="h-6 w-6 mx-auto text-slate-400 dark:text-slate-500 mb-1.5" />
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {uploading ? "Uploading…" : "Or upload an existing file"}
          </div>
          <div className="text-xs text-slate-500 mt-1">PDF, JPG, PNG, HEIC, WebP · max 25 MB · drag-drop or click to browse</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            className="hidden"
            onChange={(e) => upload(e.target.files)}
            data-testid="nts-paperwork-file-input"
          />
        </div>
        </div>

        {/* Existing attachments */}
        <div className="mt-2">
          <div className="uppercase-label mb-2">
            Attached files
            {docs.length > 0 && <span className="ml-1">({docs.length})</span>}
          </div>
          {loading && <div className="text-sm text-slate-500 py-4 text-center">Loading…</div>}
          {!loading && docs.length === 0 && (
            <div className="text-sm text-slate-400 py-4 text-center" data-testid="nts-paperwork-empty">
              No paperwork attached yet.
            </div>
          )}
          {!loading && docs.length > 0 && (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-700 rounded-md">
              {docs.map((d) => {
                const isPdf = d.content_type === "application/pdf";
                const Icon = isPdf ? FileText : ImageIcon;
                const href = buildAuthedUrl(d.url);
                return (
                  <li
                    key={d.id}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm"
                    data-testid={`nts-paperwork-row-${d.id}`}
                  >
                    <Icon className={cn("h-5 w-5 shrink-0", isPdf ? "text-rose-600" : "text-blue-600")} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">{d.filename}</div>
                      <div className="text-xs text-slate-500">
                        {fmtSize(d.size_bytes)} · uploaded {fmtDate(d.uploaded_at)}
                        {d.uploaded_by ? ` by ${d.uploaded_by}` : ""}
                      </div>
                    </div>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100 p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/40"
                      title="Open in new tab"
                      data-testid={`nts-paperwork-open-${d.id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    {canDelete && (
                      <button
                        onClick={() => doDelete(d)}
                        className="text-slate-400 hover:text-rose-600 p-1.5 rounded hover:bg-rose-50 dark:hover:bg-rose-900/40"
                        title="Delete (admin/dispatcher only)"
                        data-testid={`nts-paperwork-delete-${d.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
